import { useState, useEffect } from 'react';
import { X, Plus, MapPin, TrendingUp, Minus, AlertTriangle } from 'lucide-react';
import { inventoryApi, type ProductResponse, type LocationResponse } from '../../api/inventoryApi';
import SearchableSelect from '../../components/ui/SearchableSelect';

type OperationType = 'add' | 'remove';

interface StockOperationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    salonId: string;
    products: ProductResponse[];
    locations: LocationResponse[];
    operation: OperationType;
    preselectedProduct?: ProductResponse | null;
    preselectedLocation?: LocationResponse | null;
}

const StockOperationModal = ({
    isOpen,
    onClose,
    onSuccess,
    salonId,
    products,
    locations,
    operation,
    preselectedProduct,
    preselectedLocation
}: StockOperationModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [quantity, setQuantity] = useState('');
    const [notes, setNotes] = useState('');

    const isAdding = operation === 'add';

    // Computed values
    const selectedProduct = products.find(p => p.id === selectedProductId);
    const currentStock = selectedProduct?.stockByLocation.find(s => s.locationId === selectedLocationId)?.quantity ?? 0;

    // Initialize
    useEffect(() => {
        if (preselectedProduct) {
            setSelectedProductId(preselectedProduct.id);
        }
        if (preselectedLocation) {
            setSelectedLocationId(preselectedLocation.id);
        } else if (locations.length > 0) {
            // Default to first location or the default one
            const defaultLocation = locations.find(l => l.isDefault) || locations[0];
            setSelectedLocationId(defaultLocation.id);
        }
    }, [preselectedProduct, preselectedLocation, locations]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            if (!preselectedProduct) {
                setSelectedProductId('');
            }
            if (!preselectedLocation) {
                setSelectedLocationId(locations.find(l => l.isDefault)?.id || locations[0]?.id || '');
            }
            setQuantity('');
            setNotes('');
            setError(null);
        }
    }, [isOpen, preselectedProduct, preselectedLocation, locations]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedProductId || !selectedLocationId || !quantity) {
            setError('Por favor completa todos los campos');
            return;
        }

        const qty = parseInt(quantity);
        if (qty <= 0) {
            setError('La cantidad debe ser mayor a 0');
            return;
        }

        if (!isAdding && qty > currentStock) {
            setError(`Stock insuficiente. Disponible: ${currentStock}`);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const request = {
                productId: selectedProductId,
                locationId: selectedLocationId,
                quantity: qty,
                notes: notes || undefined,
            };

            if (isAdding) {
                await inventoryApi.addStock(salonId, request);
            } else {
                await inventoryApi.removeStock(salonId, request);
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error(`Error ${operation} stock:`, err);
            setError(err.response?.data?.message || `Error al ${isAdding ? 'agregar' : 'retirar'} stock`);
        } finally {
            setIsLoading(false);
        }
    };

    // Quick quantity buttons
    const quickAmounts = [1, 5, 10, 25, 50, 100];

    if (!isOpen) return null;

    const headerGradient = isAdding
        ? 'from-emerald-600 to-green-600'
        : 'from-orange-600 to-red-600';

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className={`bg-gradient-to-r ${headerGradient} px-6 py-4 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            {isAdding ? (
                                <Plus className="w-5 h-5 text-white" />
                            ) : (
                                <Minus className="w-5 h-5 text-white" />
                            )}
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-white">
                                {isAdding ? 'Agregar Stock' : 'Retirar Stock'}
                            </h2>
                            <p className="text-white/80 text-sm">
                                {isAdding ? 'Registrar entrada de productos' : 'Registrar salida de productos'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    <div className="space-y-5">
                        {/* Product Selection */}
                        <SearchableSelect
                            label="Producto"
                            placeholder="Selecciona un producto..."
                            options={products.map(product => ({
                                value: product.id,
                                label: `${product.name} - ${product.sku} (${product.totalStock} ${product.unit})`
                            }))}
                            value={selectedProductId}
                            onChange={(val) => setSelectedProductId(val)}
                            required
                        />

                        {/* Location Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <MapPin className="w-4 h-4 inline mr-1" />
                                Ubicación
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {locations.map(location => {
                                    const stockHere = selectedProduct?.stockByLocation.find(s => s.locationId === location.id)?.quantity ?? 0;
                                    return (
                                        <button
                                            key={location.id}
                                            type="button"
                                            onClick={() => setSelectedLocationId(location.id)}
                                            className={`p-3 rounded-xl border-2 text-left transition-all ${selectedLocationId === location.id
                                                ? isAdding ? 'border-green-500 bg-green-50' : 'border-orange-500 bg-orange-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <p className="font-medium text-gray-900">{location.name}</p>
                                            <p className="text-sm text-gray-500">
                                                {stockHere} {selectedProduct?.unit || 'unidades'}
                                            </p>
                                            {location.isDefault && (
                                                <span className="text-xs text-blue-600">Principal</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Current Stock Indicator */}
                        {selectedProduct && selectedLocationId && (
                            <div className={`p-4 rounded-xl ${isAdding ? 'bg-green-50 border border-green-100' : 'bg-orange-50 border border-orange-100'}`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Stock actual aquí:</span>
                                    <span className={`font-bold text-lg ${isAdding ? 'text-green-700' : 'text-orange-700'}`}>
                                        {currentStock} {selectedProduct.unit}
                                    </span>
                                </div>
                                {!isAdding && currentStock === 0 && (
                                    <p className="text-red-500 text-sm mt-1">⚠️ No hay stock en esta ubicación</p>
                                )}
                            </div>
                        )}

                        {/* Quantity */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Cantidad a {isAdding ? 'agregar' : 'retirar'}
                            </label>

                            {/* Quick Buttons */}
                            <div className="flex flex-wrap gap-2 mb-3">
                                {quickAmounts.map(amount => (
                                    <button
                                        key={amount}
                                        type="button"
                                        onClick={() => setQuantity(amount.toString())}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${quantity === amount.toString()
                                            ? isAdding ? 'bg-green-600 text-white' : 'bg-orange-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        {isAdding ? '+' : '-'}{amount}
                                    </button>
                                ))}
                            </div>

                            <input
                                type="number"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                placeholder="Ingresa cantidad..."
                                min="1"
                                max={!isAdding ? currentStock : undefined}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-2xl font-bold"
                                required
                            />
                        </div>

                        {/* Projected Result */}
                        {selectedProduct && selectedLocationId && quantity && parseInt(quantity) > 0 && (
                            <div className="flex items-center justify-center gap-4 p-4 bg-gray-50 rounded-xl">
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">Actual</p>
                                    <p className="text-xl font-bold text-gray-700">{currentStock}</p>
                                </div>
                                <TrendingUp className={`w-6 h-6 ${isAdding ? 'text-green-600' : 'text-orange-600 rotate-180'}`} />
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">Después</p>
                                    <p className={`text-xl font-bold ${isAdding ? 'text-green-600' : 'text-orange-600'}`}>
                                        {isAdding ? currentStock + parseInt(quantity) : currentStock - parseInt(quantity)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notas (opcional)
                            </label>
                            <input
                                type="text"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={isAdding ? 'Ej: Compra a proveedor' : 'Ej: Venta, Uso interno'}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || (!isAdding && currentStock === 0)}
                            className={`px-6 py-2 bg-gradient-to-r ${headerGradient} text-white rounded-lg transition-all disabled:opacity-50 flex items-center gap-2`}
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Procesando...
                                </>
                            ) : (
                                <>
                                    {isAdding ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                                    {isAdding ? 'Agregar' : 'Retirar'} Stock
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StockOperationModal;
